import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { CHECK_STAGE_LABEL, type CheckStage } from '@hcc/types';
import { formatDateTime, formatDate } from '../../../lib/dateFmt';
import { Pill } from '../../../components/ui/Pill';
import { Button } from '../../../components/ui/Button';
import type { AdminCheckRow } from '../../../hooks/useAdminChecks';

const STAGE_TONE: Record<CheckStage, 'success' | 'warning' | 'danger' | 'info' | 'accent' | 'neutral'> = {
  not_applied: 'neutral',
  applied: 'info',
  processed: 'warning',
  verified: 'success',
  changes_requested: 'danger',
};

/** The timestamp that explains the row's current stage. */
function stageDetail(row: AdminCheckRow): string | null {
  switch (row.stage) {
    case 'applied':
      return row.applied_at ? `Applied ${formatDateTime(row.applied_at)}` : null;
    case 'processed':
      return row.processed_at ? `Processed ${formatDateTime(row.processed_at)}` : null;
    case 'verified':
      return row.verified_at ? `Verified ${formatDateTime(row.verified_at)}` : null;
    case 'not_applied':
      return `Assigned ${formatDateTime(row.assigned_at)}`;
    case 'changes_requested':
      return null;
  }
}

/**
 * One person in the screening-check list: identity, stage pill, the
 * timestamp behind the stage, and the stage-appropriate HR actions.
 * Presentational; the page owns the mutations and modals.
 */
export function CheckRow({
  row,
  busy,
  onApply,
  onProcess,
  onVerify,
  onSendBack,
}: {
  row: AdminCheckRow;
  busy: boolean;
  onApply: () => void;
  onProcess: () => void;
  onVerify: () => void;
  onSendBack: () => void;
}) {
  const detail = stageDetail(row);
  return (
    <li className="px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/admin/people/${row.person.id}`}
              className="truncate text-sm font-semibold hover:text-accent"
            >
              {row.person.full_name}
            </Link>
            <Pill tone={STAGE_TONE[row.stage]} size="sm">
              {CHECK_STAGE_LABEL[row.stage]}
            </Pill>
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-1 text-xs text-fg-muted">
            <span className="truncate">{row.person.email}</span>
            {row.person.position ? (
              <>
                <ChevronRight className="h-3 w-3" />
                <span>{row.person.position}</span>
              </>
            ) : null}
            {row.person.start_date ? (
              <>
                <ChevronRight className="h-3 w-3" />
                <span>Starts {formatDate(row.person.start_date)}</span>
              </>
            ) : null}
          </div>
          {detail ? (
            <div className="mt-1 text-[11px] tabular-nums text-fg-muted">{detail}</div>
          ) : null}
          {row.stage === 'changes_requested' && row.rejection_reason ? (
            <p className="mt-1 rounded-token-sm bg-danger-soft/15 px-2 py-1 text-xs text-danger-soft-fg">
              Sent back: {row.rejection_reason}
            </p>
          ) : null}
          {row.stage === 'processed' ? (
            <p className="mt-1 text-xs text-fg-dim">
              Waiting for the certificate to arrive; they bring the original on their start day.
            </p>
          ) : null}
        </div>

        {row.stage !== 'verified' ? (
          <div className="flex shrink-0 flex-col gap-1.5">
            {row.stage === 'not_applied' || row.stage === 'changes_requested' ? (
              // The employee normally ticks this on /me; HR can record it
              // on their behalf when it arrives by email or word of mouth.
              <Button variant="secondary" size="sm" onClick={onApply} loading={busy}>
                Mark as applied
              </Button>
            ) : null}
            {row.stage === 'applied' ? (
              <Button variant="primary" size="sm" onClick={onProcess} loading={busy}>
                Mark processed
              </Button>
            ) : null}
            {row.stage === 'processed' ? (
              <Button variant="success" size="sm" onClick={onVerify} loading={busy}>
                Certificate verified
              </Button>
            ) : null}
            {row.stage === 'applied' || row.stage === 'processed' ? (
              <Button variant="secondary" size="sm" onClick={onSendBack} disabled={busy}>
                Send back
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </li>
  );
}
